import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_URL } from '../config.ts';

export interface SiteSettings {
  location: string;
  email: string;
  phone: string;
  githubUrl: string;
  youtubeUrl: string;
}

// Значения на случай, пока API не ответил или недоступен
export const DEFAULT_SETTINGS: SiteSettings = {
  location: 'Казань, Татарстан, Россия',
  email: '',
  phone: '+7 927 244-40-51',
  githubUrl: 'https://github.com/timurtm72',
  youtubeUrl: 'https://youtube.com/channel/UCGnkZF95JhfO7tGaB-oHC6A',
};

// null — ещё не загружено или API недоступен (тогда страницы показывают "—"/скрывают блок)
export interface SiteStats {
  projectsCount: number | null;
  experienceYears: number | null;
}

interface ContextValue {
  settings: SiteSettings;
  stats: SiteStats;
  reload: () => Promise<void>;
}

const SiteSettingsContext = createContext<ContextValue>({
  settings: DEFAULT_SETTINGS,
  stats: { projectsCount: null, experienceYears: null },
  reload: async () => {},
});

interface JobPeriod {
  startDate: string;
  endDate: string | null;
}

// Суммарный стаж в полных годах: периоды объединяются, поэтому работа
// по совместительству (пересекающиеся места) не считается дважды.
export const calcExperienceYears = (jobs: JobPeriod[]): number => {
  const now = Date.now();
  const periods = jobs
    .map((j) => [Date.parse(j.startDate), j.endDate ? Date.parse(j.endDate) : now] as [number, number])
    .filter(([s, e]) => !isNaN(s) && !isNaN(e) && e > s)
    .sort((a, b) => a[0] - b[0]);

  let totalMs = 0;
  let curStart = 0;
  let curEnd = 0;
  periods.forEach(([s, e], i) => {
    if (i === 0 || s > curEnd) {
      totalMs += curEnd - curStart;
      curStart = s;
      curEnd = e;
    } else if (e > curEnd) {
      curEnd = e;
    }
  });
  totalMs += curEnd - curStart;

  return Math.floor(totalMs / (365.25 * 24 * 3600 * 1000));
};

export const SiteSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [stats, setStats] = useState<SiteStats>({ projectsCount: null, experienceYears: null });

  const reload = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/site-settings`);
      if (res.ok) {
        setSettings({ ...DEFAULT_SETTINGS, ...(await res.json()) });
      }
    } catch (e) {
      console.error('Не удалось загрузить настройки сайта', e);
    }
  }, []);

  useEffect(() => {
    reload();

    // size=1 — нужен только totalElements, а не сами проекты
    fetch(`${API_URL}/projects?size=1`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStats((s) => ({ ...s, projectsCount: d.totalElements })))
      .catch(() => {});

    fetch(`${API_URL}/work-experience`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => Array.isArray(d) && setStats((s) => ({ ...s, experienceYears: calcExperienceYears(d) })))
      .catch(() => {});
  }, [reload]);

  return (
    <SiteSettingsContext.Provider value={{ settings, stats, reload }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);

// "+7 (927) 244-40-51" -> "tel:+79272444051"
export const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

// "https://github.com/timurtm72" -> "github.com/timurtm72"
export const shortUrl = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
